'use client';

import { useEffect, useRef, useState } from 'react';
import { Accordion } from 'rizzui';

const AUTO_ADVANCE_MS = 5000;

const advantageItems = [
  {
    title: 'Single Point of Contact',
    description:
      'One dedicated team manages planning, execution, tracking, and support, no hand-offs and no confusion.',
    icon: '/landing/assets/images/shipsmart/icons/user.svg',
  },
  {
    title: 'Scalable Logistics Solutions',
    description:
      'From a single lane to a nationwide program, our asset-plus-network model flexes with your growth.',
    icon: '/landing/assets/images/shipsmart/icons/chart.svg',
  },
  {
    title: 'Reduced Operational Burden',
    description:
      'We absorb carrier sourcing, compliance, execution, and tracking complexity so your team does not have to.',
    icon: '/landing/assets/images/shipsmart/icons/box.svg',
  },
  {
    title: 'Improved Efficiency',
    description:
      'Optimized routing and dedicated capacity reduce transit times, delays, and avoidable disruptions.',
    icon: '/landing/assets/images/shipsmart/icons/growth-box.svg',
  },
  {
    title: 'Lower Transportation Costs',
    description:
      'Asset-based operations plus a vetted network drive long-term cost stability and dependable coverage.',
    icon: '/landing/assets/images/shipsmart/icons/money.svg',
  },
] as const;

export function HomeAdvantageAccordion() {
  const [openIndex, setOpenIndex] = useState(0);
  const [rotationVersion, setRotationVersion] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const listRef = useRef<HTMLUListElement>(null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    const list = listRef.current;
    if (!list) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsInView(entry.isIntersecting),
      { threshold: 0.25 }
    );

    observer.observe(list);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    const prefersReducedMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
    const usesCompactLayout = window.matchMedia(
      '(max-width: 991.98px)'
    ).matches;

    if (!isInView || prefersReducedMotion || usesCompactLayout) return;

    timeoutRef.current = window.setTimeout(() => {
      timeoutRef.current = null;
      setOpenIndex((current) => (current + 1) % advantageItems.length);
    }, AUTO_ADVANCE_MS);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isInView, openIndex, rotationVersion]);

  function openItem(index: number) {
    setOpenIndex(index);
    setRotationVersion((current) => current + 1);
  }

  return (
    <ul className="journey-list journey-accordion" ref={listRef}>
      {advantageItems.map((item, index) => {
        const isOpen = openIndex === index;
        const bodyId = `advantage-accordion-body-${index}`;

        return (
          <Accordion
            as="li"
            className={[
              'journey-list__item journey-accordion__item',
              isOpen ? 'is-open' : '',
            ].join(' ')}
            defaultOpen={isOpen}
            duration={280}
            key={`${item.title}-${isOpen ? 'open' : 'closed'}`}
          >
            <Accordion.Header
              aria-controls={bodyId}
              aria-expanded={isOpen}
              className="journey-accordion__header"
              onClick={() => openItem(index)}
            >
              {({ open }) => (
                <>
                  <span className="journey-list__date">
                    <img
                      src={item.icon}
                      alt=""
                      loading="lazy"
                      decoding="async"
                    />
                  </span>

                  <span className="h6 journey-list__title neutral-0">
                    {item.title}
                  </span>

                  <span
                    className={[
                      'journey-list__link',
                      open ? 'is-open' : '',
                    ].join(' ')}
                    aria-hidden="true"
                  >
                    <svg
                      width="11"
                      height="11"
                      viewBox="0 0 11 11"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M0.21967 9.40717C-0.0732232 9.70006 -0.0732232 10.1749 0.21967 10.4678C0.512563 10.7607 0.987437 10.7607 1.28033 10.4678L0.21967 9.40717ZM10.6875 0.75C10.6875 0.335786 10.3517 2.97145e-09 9.9375 1.50485e-07L3.1875 -2.70983e-07C2.77329 -2.70983e-07 2.4375 0.335786 2.4375 0.75C2.4375 1.16421 2.77329 1.5 3.1875 1.5H9.1875V7.5C9.1875 7.91421 9.52329 8.25 9.9375 8.25C10.3517 8.25 10.6875 7.91421 10.6875 7.5L10.6875 0.75ZM0.75 9.9375L1.28033 10.4678L10.4678 1.28033L9.9375 0.75L9.40717 0.21967L0.21967 9.40717L0.75 9.9375Z"
                        fill="currentColor"
                      />
                    </svg>
                  </span>
                </>
              )}
            </Accordion.Header>

            <Accordion.Body id={bodyId} className="journey-accordion__body">
              <p className="journey-list__desc">{item.description}</p>
            </Accordion.Body>
          </Accordion>
        );
      })}
    </ul>
  );
}
